import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:4040/api";

const AddVessel = () => {
  const navigate = useNavigate();

  const [zones, setZones] = useState([
    {
      id: 1,
      name: "Hold 1",
      code: "H1",
      type: "livestock",
      maxWeight: "",
      maxUnits: "",
      allowedCargo: "",
      notes: "",
    },
  ]);

  const [form, setForm] = useState({
    name: "",
    code: "",
    type: "",
    status: "active",
    routes: "",
    marineTraffic: "",
    notes: "",
    length: "",
    beam: "",
    draft: "",
    dwt: "",
    cargoCapacity: "",
    speed: "",
    mainCrane: "",
    hiabCrane: "",
    reeferPlugs: "",
    rampCapacity: "",
    deckLoading: "",
    maxBookingWeight: "",
    maxLivestockAllocation: "",
    maxFreightAllocation: "",
    maxTEU: "",
    mixedCargoAllowed: "Yes",
    livestockAndFreightTogether: "Yes",
    safetyBuffer: "",
    operationalBuffer: "",
  });

  const cleanNumber = (value) =>
    Number(String(value || "").replace(/[^\d.]/g, "")) || 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateZone = (id, field, value) => {
    setZones((prev) =>
      prev.map((zone) => (zone.id === id ? { ...zone, [field]: value } : zone))
    );
  };

  const addZone = () => {
    setZones((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: "",
        code: "",
        type: "freight",
        maxWeight: "",
        maxUnits: "",
        allowedCargo: "",
        notes: "",
      },
    ]);
  };

  const removeZone = (id) => {
    setZones((prev) => prev.filter((zone) => zone.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Vessel name is required.");
      return;
    }

    try {
      const payload = {
        name: form.name,
        code: form.code,
        status: form.status,
        vesselType: form.type || "coastal-freight",
        registrationNumber: form.code,

        lengthM: cleanNumber(form.length),
        beamM: cleanNumber(form.beam),
        draftM: cleanNumber(form.draft),
        grossTonnage: cleanNumber(form.dwt),

        maxWeightKg: cleanNumber(form.maxBookingWeight),
        maxLivestockHead: cleanNumber(form.maxLivestockAllocation),
        maxCubeM3: cleanNumber(form.maxFreightAllocation),

        notes: form.notes,

        zones: zones
          .filter((zone) => zone.name.trim())
          .map((zone) => ({
            name: zone.name,
            type: zone.type || "mixed",
            maxWeightKg: cleanNumber(zone.maxWeight),
            maxHeadCount: cleanNumber(zone.maxUnits),
            notes: zone.notes || "",
          })),
      };

      const res = await fetch(`${API_BASE}/vessels`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to create vessel");
      }

      navigate("/dashboard/settings");
    } catch (err) {
      console.error(err);
      alert("Could not create vessel. Check API and required fields.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/dashboard/settings")}
              className="mb-4 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              ← Back
            </button>

            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              Add Vessel
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Create a vessel profile with technical specs, booking capacity, and
              zone setup.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard/settings")}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              form="add-vessel-form"
              className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              Create Vessel
            </button>
          </div>
        </div>

        <form id="add-vessel-form" onSubmit={handleSubmit}>
          <div className="grid gap-6 xl:grid-cols-[1.7fr_0.9fr]">
            <div className="space-y-6">
              <SectionCard
                title="Basic Details"
                description="Core vessel profile, status, routes, and operating notes."
              >
                <SettingsGrid>
                  <Field label="Vessel Name">
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Internal Code">
                    <input
                      name="code"
                      value={form.code}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Vessel Type">
                    <input
                      name="type"
                      value={form.type}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Status">
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </Field>

                  <Field label="Default Operating Routes">
                    <input
                      name="routes"
                      value={form.routes}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="MarineTraffic Link">
                    <input
                      name="marineTraffic"
                      value={form.marineTraffic}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>
                </SettingsGrid>

                <Field label="Notes">
                  <textarea
                    name="notes"
                    rows={3}
                    value={form.notes}
                    onChange={handleChange}
                    className="input min-h-[110px] resize-y"
                  />
                </Field>
              </SectionCard>

              <SectionCard
                title="Technical Specs"
                description="Technical dimensions, lifting gear, and vessel handling capability."
              >
                <SettingsGrid>
                  <Field label="Length Overall / LOA metres">
                    <input
                      name="length"
                      value={form.length}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Beam / Breadth metres">
                    <input
                      name="beam"
                      value={form.beam}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Draft metres">
                    <input
                      name="draft"
                      value={form.draft}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Gross Tonnage / DWT">
                    <input
                      name="dwt"
                      value={form.dwt}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Cargo Capacity">
                    <input
                      name="cargoCapacity"
                      value={form.cargoCapacity}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Speed">
                    <input
                      name="speed"
                      value={form.speed}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Main Crane Capacity">
                    <input
                      name="mainCrane"
                      value={form.mainCrane}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Secondary Lifting Capacity">
                    <input
                      name="hiabCrane"
                      value={form.hiabCrane}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Reefer Plugs">
                    <input
                      name="reeferPlugs"
                      value={form.reeferPlugs}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Ramp Capacity">
                    <input
                      name="rampCapacity"
                      value={form.rampCapacity}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Deck Loading Rating">
                    <input
                      name="deckLoading"
                      value={form.deckLoading}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>
                </SettingsGrid>
              </SectionCard>

              <SectionCard
                title="Booking Capacity"
                description="Set vessel booking limits and allocation rules."
              >
                <SettingsGrid>
                  <Field label="Max Booking Weight KG">
                    <input
                      name="maxBookingWeight"
                      value={form.maxBookingWeight}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Max Livestock Head">
                    <input
                      name="maxLivestockAllocation"
                      value={form.maxLivestockAllocation}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Max Freight / Cube">
                    <input
                      name="maxFreightAllocation"
                      value={form.maxFreightAllocation}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Max TEU">
                    <input
                      name="maxTEU"
                      value={form.maxTEU}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Mixed Cargo Allowed">
                    <select
                      name="mixedCargoAllowed"
                      value={form.mixedCargoAllowed}
                      onChange={handleChange}
                      className="input"
                    >
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </Field>

                  <Field label="Livestock + Freight Together">
                    <select
                      name="livestockAndFreightTogether"
                      value={form.livestockAndFreightTogether}
                      onChange={handleChange}
                      className="input"
                    >
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </Field>

                  <Field label="Safety Buffer %">
                    <input
                      name="safetyBuffer"
                      value={form.safetyBuffer}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Operational Buffer %">
                    <input
                      name="operationalBuffer"
                      value={form.operationalBuffer}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>
                </SettingsGrid>
              </SectionCard>

              <SectionCard
                title="Zones / Holds"
                description="Configure capacity zones for livestock, freight, or mixed use."
                action={
                  <button
                    type="button"
                    onClick={addZone}
                    className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                  >
                    Add Zone
                  </button>
                }
              >
                <div className="space-y-4">
                  {zones.map((zone, index) => (
                    <div
                      key={zone.id}
                      className="rounded-3xl border border-slate-200 bg-slate-50 p-5"
                    >
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <h3 className="font-semibold text-slate-900">
                          {zone.name || `Zone ${index + 1}`}
                        </h3>

                        {zones.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => removeZone(zone.id)}
                            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        <Field label="Zone Name">
                          <input
                            className="input"
                            value={zone.name}
                            onChange={(e) =>
                              updateZone(zone.id, "name", e.target.value)
                            }
                          />
                        </Field>

                        <Field label="Zone Code">
                          <input
                            className="input"
                            value={zone.code}
                            onChange={(e) =>
                              updateZone(zone.id, "code", e.target.value)
                            }
                          />
                        </Field>

                        <Field label="Zone Type">
                          <select
                            className="input"
                            value={zone.type}
                            onChange={(e) =>
                              updateZone(zone.id, "type", e.target.value)
                            }
                          >
                            <option value="livestock">Livestock</option>
                            <option value="freight">Freight</option>
                            <option value="mixed">Mixed</option>
                          </select>
                        </Field>

                        <Field label="Max Weight KG">
                          <input
                            className="input"
                            value={zone.maxWeight}
                            onChange={(e) =>
                              updateZone(zone.id, "maxWeight", e.target.value)
                            }
                          />
                        </Field>

                        <Field label="Max Units / Head Count">
                          <input
                            className="input"
                            value={zone.maxUnits}
                            onChange={(e) =>
                              updateZone(zone.id, "maxUnits", e.target.value)
                            }
                          />
                        </Field>

                        <Field label="Allowed Cargo Type">
                          <input
                            className="input"
                            value={zone.allowedCargo}
                            onChange={(e) =>
                              updateZone(zone.id, "allowedCargo", e.target.value)
                            }
                          />
                        </Field>
                      </div>

                      <div className="mt-4">
                        <Field label="Zone Notes">
                          <textarea
                            rows={2}
                            className="input min-h-[90px] resize-y"
                            value={zone.notes}
                            onChange={(e) =>
                              updateZone(zone.id, "notes", e.target.value)
                            }
                          />
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <div className="space-y-6">
              <AsideCard title="Vessel Summary">
                <SummaryRow label="Name" value={form.name || "—"} />
                <SummaryRow label="Code" value={form.code || "—"} />
                <SummaryRow label="Type" value={form.type || "—"} />
                <SummaryRow label="Status" value={form.status || "active"} />
                <SummaryRow label="Routes" value={form.routes || "—"} />
                <SummaryRow
                  label="MarineTraffic"
                  value={form.marineTraffic || "—"}
                />
              </AsideCard>

              <AsideCard title="Capacity Snapshot">
                <SummaryRow
                  label="Max Weight KG"
                  value={form.maxBookingWeight || "—"}
                />
                <SummaryRow
                  label="Max Livestock"
                  value={form.maxLivestockAllocation || "—"}
                />
                <SummaryRow
                  label="Max Freight / Cube"
                  value={form.maxFreightAllocation || "—"}
                />
                <SummaryRow label="Max TEU" value={form.maxTEU || "—"} />
                <SummaryRow label="Zones" value={String(zones.length)} />
              </AsideCard>

              <AsideCard title="Operating Rules">
                <SummaryRow
                  label="Mixed Cargo Allowed"
                  value={form.mixedCargoAllowed || "Yes"}
                />
                <SummaryRow
                  label="Livestock + Freight"
                  value={form.livestockAndFreightTogether || "Yes"}
                />
                <SummaryRow
                  label="Safety Buffer"
                  value={form.safetyBuffer || "—"}
                />
                <SummaryRow
                  label="Operational Buffer"
                  value={form.operationalBuffer || "—"}
                />
              </AsideCard>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.9rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .input::placeholder {
          color: rgb(148 163 184);
        }

        .input:hover {
          border-color: rgb(148 163 184);
        }

        .input:focus {
          border-color: rgb(15 23 42);
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
        }
      `}</style>
    </div>
  );
};

const SectionCard = ({ title, description, children, action }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const SettingsGrid = ({ children }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-slate-700">
      {label}
    </label>
    {children}
  </div>
);

const AsideCard = ({ title, children }) => (
  <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
      {title}
    </h2>
    <div className="mt-4 space-y-3">{children}</div>
  </aside>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
    <span className="text-sm font-medium text-slate-500">{label}</span>
    <span className="max-w-[60%] break-words text-right text-sm font-semibold text-slate-900">
      {value}
    </span>
  </div>
);

export default AddVessel;